import React, { useState, useEffect } from "react";
import { Gift, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { claimRewardApi } from "../http/http";

const ClaimRewardsBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [points, setPoints] = useState(0);
  const [claimState, setClaimState] = useState("idle"); // idle, loading, success, error
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const canClaim = localStorage.getItem("canClaimRewards") === "true";
    const canClaimPoints = localStorage.getItem("canClaimPoints");
    if (canClaim && canClaimPoints) {
      setPoints(Number(canClaimPoints));
      setShowBanner(true);
    }
  }, []);

  const handleClaim = async (e) => {
    e.stopPropagation();
    try {
      setClaimState("loading");
      const msisdn = localStorage.getItem("msisdn");
      const token = localStorage.getItem("token");
      const deviceId = localStorage.getItem("deviceId");

      if (!msisdn) {
        throw new Error("Phone number not found. Please log in.");
      }

      const response = await claimRewardApi(msisdn, token, deviceId);

      if (response.status === 200 || response.status === 201) {
        const successMsg = typeof response.data === "string" 
          ? response.data 
          : response.data?.message || `Claimed Points : ${points}`;
        
        setMsg(successMsg);
        setClaimState("success");

        // Clear rewards status from localStorage after a successful claim
        localStorage.removeItem("canClaimRewards");
        localStorage.removeItem("canClaimPoints");

        // Automatically hide the banner after 4 seconds
        setTimeout(() => {
          setShowBanner(false);
        }, 4000);
      } else {
        throw new Error(response.data?.message || "Failed to claim.");
      }
    } catch (error) {
      console.error(error);
      const errMsg = error.response?.data?.message || error.response?.data || error.message || "Claim failed.";
      setMsg(errMsg);
      setClaimState("error");
    }
  };

  if (!showBanner) return null;

  return (
    <div className="w-full bg-gradient-to-r from-cyan-950/40 via-slate-900/50 to-cyan-950/40 border border-[#00D9FF]/20 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-lg relative overflow-hidden transition-all duration-300">
      {/* Background radial glow */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-[#00D9FF]/5 rounded-full blur-xl pointer-events-none"></div>

      {claimState === "idle" && (
        <>
          <div className="flex items-center gap-3 text-left">
            <div className="p-2 bg-[#00D9FF]/10 rounded-xl border border-[#00D9FF]/30 flex-shrink-0 animate-pulse">
              <Gift className="w-6 h-6 text-[#00D9FF]" />
            </div>
            <div>
              <h4 className="text-white text-sm font-black tracking-wide">
                Exclusive Reward Pending! 🎁
              </h4>
              <p className="text-gray-300 text-xs mt-0.5 leading-snug">
                You have <strong className="text-[#00D9FF]">{points} Points</strong> waiting for you. Claim them now!
              </p>
            </div>
          </div>
          <button
            onClick={handleClaim}
            className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-[#00D9FF] to-[#00A3FF] text-black font-black text-xs rounded-xl shadow-md shadow-cyan-500/25 hover:shadow-cyan-500/45 hover:brightness-110 active:scale-95 transition-all border-none cursor-pointer uppercase tracking-wider flex-shrink-0"
          >
            Claim Now
          </button>
        </>
      )}

      {claimState === "loading" && (
        <div className="w-full flex items-center justify-center gap-3 py-1">
          <Loader2 className="w-5 h-5 text-[#00D9FF] animate-spin" />
          <span className="text-gray-300 text-xs font-bold uppercase tracking-wider animate-pulse">
            Claiming your points...
          </span>
        </div>
      )}

      {claimState === "success" && (
        <div className="w-full flex items-center justify-between gap-3 text-left animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/10 rounded-xl border border-emerald-500/30 flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h4 className="text-emerald-400 text-sm font-black tracking-wide">
                Successfully Claimed! 🎉
              </h4>
              <p className="text-gray-300 text-xs mt-0.5 font-semibold">
                {msg}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowBanner(false)}
            className="px-3 py-1.5 bg-transparent border border-emerald-500/50 text-emerald-400 hover:text-white hover:bg-emerald-500/20 text-xs font-bold rounded-lg cursor-pointer transition-all"
          >
            Dismiss
          </button>
        </div>
      )}

      {claimState === "error" && (
        <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-3 text-left animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-rose-500/10 rounded-xl border border-rose-500/30 flex-shrink-0">
              <XCircle className="w-6 h-6 text-rose-400" />
            </div>
            <div>
              <h4 className="text-rose-400 text-sm font-black tracking-wide">
                Claim Failed
              </h4>
              <p className="text-gray-300 text-xs mt-0.5 leading-snug">
                {msg}
              </p>
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={handleClaim}
              className="flex-1 sm:flex-none px-3 py-1.5 bg-rose-500 text-white text-xs font-bold rounded-lg border-none cursor-pointer hover:bg-rose-600 transition-all uppercase tracking-wider"
            >
              Retry
            </button>
            <button
              onClick={() => {
                localStorage.removeItem("canClaimRewards");
                localStorage.removeItem("canClaimPoints");
                setShowBanner(false);
              }}
              className="flex-1 sm:flex-none px-3 py-1.5 bg-transparent border border-gray-600 text-gray-400 text-xs font-bold rounded-lg cursor-pointer hover:text-white transition-all"
            >
              Skip
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimRewardsBanner;
